"use client";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card.tsx";
import { Button } from "../../../components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/ui/dropdown-menu.tsx";
import { MoreVertical, FileDown, Trash2 } from "lucide-react";
import { Order } from "../types/order.ts";
import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../../src/App.js";
// import { QuotationTemplate } from "./QuotationTemplate.tsx";
// import { QuotationTemplate as QuotationTemplatePrint } from "./QuotationTemplate_ForPrint.tsx";
import { QuotationTemplate } from "./QuotationTemplate.tsx";
import { QuotationTemplate as QuotationTemplatePrint } from "./QuotationTemplate_ForPrint.tsx";
import { downloadPDF } from "../../../utils/pdfGenerator.ts";
import { getOrderDeliveryText } from "./orderDeliveryText";
import { getOrderRevisionBumpIntent } from "../../../utils/orderRevisionIntent";

// Helper function to format date/time to India timezone
const formatIndiaTime = (dateString: string | Date | undefined) => {
  if (!dateString || dateString === '') return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata'
  }).replace(',', '');
};

// Helper function to format date only (without time) to India timezone
const formatDateOnly = (dateString: string | Date | undefined) => {
  if (!dateString || dateString === '') return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  }).replace(/\//g, '-');
};

interface Props {
  order: Order;
  refresh: () => void;
  onEdit: (order: Order) => void;
  orderStatuses?: { id: number; label: string; color: string }[];
  isSelected?: boolean;
  onViewDetails?: (order: Order) => void;
}

export const OrderCard: React.FC<Props> = ({ order, refresh, onEdit, orderStatuses = [], isSelected = false }) => {
  const [previewColor, setPreviewColor] = useState<any>(null);
  const navigate = useNavigate();
  const { authToken = "", id = "", role = "", name = "", username = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const handleDeleteOrder = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this quotation?")) return;
    try {
      await axios.delete(`${API_BASE_URL}account/${id}/order/${order.id}/delete`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      refresh();
    } catch (err) {
      console.error("Error deleting order:", err);
      alert("Failed to delete quotation");
    }
  };

  const handleStatusChange = async (status: string) => {
    const bumpRevision = getOrderRevisionBumpIntent(order.id);
    const payload = {
      ...order,
      status,
      createdBy: username,
      revisedQuotation: bumpRevision,
    };
    try {
      await axios.put(`${API_BASE_URL}account/${id}/order/update`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      refresh();
    } catch (e) {
      console.error("Error updating order status:", e);
    }
  };

   const handleSubmit = async () => {
    const today = new Date();
    const payload = {
      ...order,
      createdBy: username,
      invoiceGenerated: true,
      invoiceDate: today.toISOString(),
      revisedQuotation: false,
    };
  
    try {
        await axios.put(`${API_BASE_URL}account/${id}/order/update`, payload, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
     
    } catch (e) {
      console.error("Error saving order:", e);
    }
  
    refresh();
  };
const handleDownloadPDF = async () => {
  try {
    const settingsRes = await axios.get(`${API_BASE_URL}account/${id}/settings`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    await downloadPDF(
      <QuotationTemplate order={order} settings={settingsRes.data} />,
      `Quotation_${order.customerName || "Order"}.pdf`
    );
  } catch (error) {
    console.error("Error downloading PDF:", error);
    alert("Failed to download PDF");
  }
};

const handleDownloadPrintFormatPDF = async () => {
  try {
    const settingsRes = await axios.get(`${API_BASE_URL}account/${id}/settings`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    await downloadPDF(
      <QuotationTemplatePrint order={order} settings={settingsRes.data} />,
      `Quotation_PrintFormat_${order.customerName || "Order"}.pdf`
    );
  } catch (error) {
    console.error("Error downloading print format PDF:", error);
    alert("Failed to download print format PDF");
  }
};

const convertToInchesWithNool = (
  value: number | string | undefined,
  uom: string | undefined,
  extraNool?: number | string
) => {
  if (!value) return { inches: "N/A", nool: 0 };

  let num = parseFloat(value.toString());

  // Convert to inches
  switch (uom) {
    case "Feet":
      num = num * 12;
      break;
    case "CM":
      num = num / 2.54;
      break;
    case "MM":
      num = num / 25.4;
      break;
    case "m":
      num = num * 39.3701;
      break;
    case "Inch":
    default:
      break;
  }

  let wholeInches = Math.floor(num);
  const decimal = num - wholeInches;

  // Round down to nearest 0.5
  const halfPart = Math.floor(decimal / 0.5) * 0.5;
  const remaining = decimal - halfPart;

  // Convert remaining to nool
  let calculatedNool = Math.round(remaining * 8);

  // Add existing nool (if any)
  const additionalNool = extraNool ? parseInt(extraNool.toString()) : 0;

  let totalNool = calculatedNool + additionalNool;

  // Carry over if nool >= 8
  if (totalNool >= 8) {
    wholeInches += Math.floor(totalNool / 8);
    totalNool = totalNool % 8;
  }

  return {
    inches: wholeInches + halfPart,
    nool: totalNool,
  };
};
const handlePrintProductionCopy = () => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow popups for this website");
    return;
  }

  let productsHTML = "";
  if (order.products && order.products.length > 0) {
    productsHTML = order.products
      .map((product: any) => {
        let specHTML = "";
        if (product.specifications) {
          specHTML = Object.entries(product.specifications)
            .map(([key, value]: [string, any]) => {
              const displayValue =
                typeof value === "object" && value?.name ? value.name : value || "N/A";
              return `
            <div class="product-field">
              <span class="product-label">${key.replace(/_/g, " ")}:</span>
              <span class="product-val">${displayValue}</span>
            </div>`;
            })
            .join("");
        }

      return `
  <div class="product">
    <div class="product-name">${product.name || "N/A"}</div>
    ${specHTML}
    
    <div class="product-field">
      <span class="product-label">Place:</span>
      <span class="product-val">${product.place || "N/A"}</span>
    </div>

    ${
      product.width
        ? `
      <div class="product-field">
        <span class="product-label">Width:</span>
        <span class="product-val">
          ${(() => {
  const converted = convertToInchesWithNool(
    product.width,
    product.width_uom,
    product.nool_width
  );

  if (converted.inches === "N/A") return "N/A";

 return `${converted.inches}${
          converted.nool > 0 ? `<sup>${converted.nool}</sup>` : ""
        } inches`;
})()
}
        </span>
      </div>`
        : ""
    }

    ${
      product.height
        ? `
      <div class="product-field">
        <span class="product-label">Height:</span>
        <span class="product-val">
          ${(() => {
  const converted = convertToInchesWithNool(
    product.height,
    product.height_uom,
    product.nool_height
  );

  if (converted.inches === "N/A") return "N/A";

  return `${converted.inches}${
          converted.nool > 0 ? `<sup>${converted.nool}</sup>` : ""
        } inches`;
})()
}
         
        </span>
      </div>`
        : ""
    }

    ${
      product.length
        ? `
      <div class="product-field">
        <span class="product-label">Length:</span>
        <span class="product-val">
          ${(() => {
  const converted = convertToInchesWithNool(
    product.length,
    product.length_uom,
    product.nool_length
  );

  if (converted.inches === "N/A") return "N/A";

  return `${converted.inches}${
          converted.nool > 0 ? `<sup>${converted.nool}</sup>` : ""
        } inches`;
})()}
        </span>
      </div>`
        : ""
    }

    <div class="product-field">
      <span class="product-label">Quantity:</span>
      <span class="product-val">${product.nos || "N/A"}</span>
    </div>
  </div>
`;
;
      })
      .join("");
  } else {
    productsHTML = "<p style='font-size: 10px;'>No products available</p>";
  }

 


  const productionCopyHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Production Copy</title>
      <style>
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            size: 4in 6in;
            margin: 0;
          }
        }
        body {
          font-family: Arial, sans-serif;
          width: 4in;
          height: 6in;
          margin: 0;
          padding: 8px;
          box-sizing: border-box;
          background: white;
        }
        .header {
          text-align: center;
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 8px;
          border-bottom: 2px solid black;
          padding-bottom: 4px;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          margin-bottom: 6px;
          line-height: 1.3;
        }
        .info-label {
          font-weight: bold;
          width: 40%;
        }
        .info-value {
          width: 55%;
          word-break: break-word;
        }
        .section-divider {
          border-top: 1px solid black;
          margin: 6px 0;
        }
        .product {
          margin-bottom: 8px;
          border: 1px solid black;
          padding: 4px;
        }
        .product-name {
          font-weight: bold;
          font-size: 11px;
          margin-bottom: 4px;
          text-align: center;
          background: #f0f0f0;
          padding: 2px;
        }
      .product-field {
  display: flex;
  font-size: 9px;
  line-height: 1.2;
  border-top: 1px solid #000;
}

      .product-label {
  font-weight: bold;
  width: 45%;
  padding: 2px 4px;
  border-right: 1px solid #000; /* 🔴 COLUMN LINE */
  box-sizing: border-box;
}
       .product-val {
  width: 55%;
  padding: 2px 4px;
  word-break: break-word;
  box-sizing: border-box;
}
      </style>
    </head>
    <body>
      <div class="header">Production Copy</div>
      
      <div class="info-row">
        <span class="info-label">Customer Name :</span>
        <span class="info-value">${order.customerName || "N/A"}</span>
      </div>
      
      <div class="info-row">
        <span class="info-label">Mobile Number :</span>
        <span class="info-value">${order.mobile || "N/A"}</span>
      </div>
      
      <div class="info-row">
        <span class="info-label">Address :</span>
        <span class="info-value">${order.address_ || "N/A"}</span>
      </div>

      <div class="info-row">
        <span class="info-label">Created On :</span>
        <span class="info-value">${formatIndiaTime(order.createdAt)}</span>
      </div>

      <div class="info-row">
        <span class="info-label">Delivery :</span>
        <span class="info-value">${getOrderDeliveryText(order) || "N/A"}</span>
      </div>

      <div class="info-row">
        <span class="info-label">Delivered By Company :</span>
        <span class="info-value">${formatDateOnly(order.toBeDelivered)}</span>
      </div>
      
      <div class="section-divider"></div>

      ${productsHTML}
    </body>
    </html>
  `;

  printWindow.document.write(productionCopyHTML);
  printWindow.document.close();

  setTimeout(() => {
    printWindow.print();
  }, 500);
};

  return (
    <Card
      className={`p-0 rounded-2xl overflow-hidden shadow-sm transition-all cursor-pointer select-none ${
        isSelected
          ? "border-2 border-[var(--sidebar-primary)] shadow-xl ring-2 ring-[var(--accent-primary)]"
          : "border-2 border-gray-200 hover:shadow-xl hover:border-[var(--accent-primary)]"
      } group`}
      onClick={() => navigate(`/panel/orders/${order.id}`)}
    >
      <CardHeader
        className="bg-gradient-to-r from-[var(--sidebar-light)] to-[var(--accent-primary)]/20 flex justify-between items-start p-3 border-b-2 border-gray-200"
      >
        <div className="flex-1">
          <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-[var(--sidebar-primary)] transition-colors">
            {order.customerName}
          </CardTitle>
          <p className="text-xs text-gray-500 mt-0.5 font-medium">
            Quotation No: <span className="text-[var(--sidebar-primary)] font-semibold">{order.quotationNumber || order.id}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(() => {
            const matched = orderStatuses.find(
              (s) => s.label?.toLowerCase() === order.status?.toLowerCase()
            );
            const bgColor = matched?.color ?? "#EAB308";
            const label = matched?.label ?? (order.status || "New");
            return (
              <span
                style={{ backgroundColor: bgColor + "22", color: bgColor, border: `1.5px solid ${bgColor}` }}
                className="px-3 py-1 rounded-full text-xs font-bold"
              >
                {label}
              </span>
            );
          })()}
          {role === "admin" && (
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-red-50 text-red-400 hover:text-red-600"
              onClick={handleDeleteOrder}
              title="Delete quotation"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
         {role !== "dealer" && <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-white" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="w-4 h-4 text-gray-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white rounded-lg shadow-lg min-w-[200px]">
              {orderStatuses.map((status) => (
                <DropdownMenuItem
                  key={status.id}
                  onClick={() => handleStatusChange(status.label)}
                  className="cursor-pointer text-gray-700 flex items-center gap-2"
                >
                  <span
                    style={{ backgroundColor: status.color }}
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  />
                  {status.label}
                </DropdownMenuItem>
              ))}
              {(role === "admin" || role === "employee") && order.status === "inprogress" && (
                <DropdownMenuItem
                  onClick={() => handlePrintProductionCopy()}
                  className="cursor-pointer hover:bg-orange-50 text-gray-700"
                >
                  Production Copy
                </DropdownMenuItem>
              )}
              {order?.quotation && (
                <DropdownMenuItem
                  onClick={() => handleDownloadPrintFormatPDF()}
                  className="cursor-pointer hover:bg-emerald-50 text-gray-700"
                >
                  Download Print Format
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </CardHeader>

      <CardContent className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
            <p className="text-xs text-gray-500 font-semibold mb-0.5">Created By</p>
            <p className="text-sm font-bold text-gray-900">{order.createdBy || "N/A"}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
            <p className="text-xs text-gray-500 font-semibold mb-0.5">Created At</p>
            <p className="text-sm font-bold text-gray-900">
              {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}
            </p>
          </div>
        </div>

        {order?.quotation && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); handleDownloadPDF(); }}
              className="flex-1 md:flex-none border-green-300 text-green-600 hover:bg-green-50 font-semibold"
            >
              <FileDown className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
